/*
 * -----------------------------------------------------------------------
 *  <copyright file="add-panel-column/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import FormControl from '@mui/material/FormControl';
import cn from 'clsx';
import React from 'react';
import { FieldSelector, Formats } from '@components/central-monitoring/field-selector';
import { STEP4_COLUMN_TYPES } from '@components/central-monitoring/panels/add-panel/step4-overview/add-panel-column/add-panel-column.data';
import { AddPanelColumnProps } from '@components/central-monitoring/panels/add-panel/step4-overview/add-panel-column/add-panel-column.interface';
import { IStudyOverviewExtraColumn } from '@components/central-monitoring/panels/add-panel/step4-overview/step4-overview.interface';
import CharactersRemaining from '@components/core/characters-remaining';
import SelectWithCategories from '@components/core/select-with-categories';
import TextField from '@components/core/text-field';
import { useTranslation } from '@hooks/index';
import { createArrayFromEnum } from '@utils/generator';
import { validateFields } from '@utils/validation';
import { RowDataSourceTable, StudyOverviewExtraColumnType } from '@types/graphql-global-types';

const AddPanelColumn = ({ canSave, updateColumn, index, column, errors, setErrors }: AddPanelColumnProps) => {
    const { t } = useTranslation();

    const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
        const error = validateFields(value, field, length, required);
        setErrors({
            ...errors,
            [`${field}${index}`]: error[field],
        });
    };

    const onChange = (fieldName: string, length?: number, required?: boolean, value?: string) => {
        let newColumn: IStudyOverviewExtraColumn;
        if (fieldName === 'dataSource') {
            newColumn = {
                ...column,
                dataSource: value,
                dataField: '',
                denominatorField: '',
                numeratorField: '',
            };
        } else if (fieldName === 'type') {
            newColumn = {
                ...column,
                type: value,
                dataSource: '',
                dataField: '',
                denominatorField: '',
                numeratorField: '',
            };
        } else {
            newColumn = { ...column, [fieldName]: value };
        }

        updateColumn(newColumn, index);
        validate(value, fieldName, length, required);
    };

    const valuesTest = createArrayFromEnum(RowDataSourceTable, '_TABLE', '').filter(
        (item: any) => item.id !== RowDataSourceTable.NONE && item.id !== RowDataSourceTable.STUDY,
    );

    return (
        <>
            <div className="type-edc-wrapper mb-0">
                <FormControl variant="outlined" className="form-control-type mr select-with-error">
                    <TextField
                        label={`${t('admin:columnValueLabel', { num: index + 1 })}*`}
                        fullWidth
                        value={column.label}
                        onChange={(event: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>) => {
                            onChange('label', 50, true, event.target?.value);
                        }}
                        placeholder={`${t('admin:enterAValue', { num: index + 1 })}*`}
                        helperText={
                            <CharactersRemaining
                                id="add-panel-name-help-text"
                                length={column.label?.length}
                                max={50}
                                error={errors[`label${index}`]}
                            />
                        }
                        error={!!errors[`label${index}`] || !!errors.text}
                        variant="outlined"
                        color="secondary"
                        className="add-panel-label"
                    />
                </FormControl>
                <FormControl variant="outlined" className="form-control-type select-with-error">
                    <SelectWithCategories
                        label={`${t('admin:columnTypeLabel', { num: index + 1 })}*`}
                        dataArray={STEP4_COLUMN_TYPES}
                        onChange={(fieldName: string) =>
                            (event: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>) => {
                                onChange(fieldName, undefined, true, event.target?.value);
                            }}
                        fieldSelect="type"
                        currentState={column.type}
                        error={errors[`type${index}`]}
                        initialState="default"
                        labelField="type"
                        filterable={false}
                        customClassName="add-panel-columnType"
                    />
                </FormControl>
            </div>
            {!!column.type && (
                <>
                    <div className="type-edc-wrapper">
                        <FormControl
                            variant="outlined"
                            className={cn('form-control-type mr select-with-error', {
                                'not-full': column.type === StudyOverviewExtraColumnType.RATE,
                            })}
                        >
                            <SelectWithCategories
                                label={`${t('admin:columnDataSourceLabel', { num: index + 1 })}*`}
                                dataArray={valuesTest}
                                onChange={(fieldName: string) =>
                                    (event: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>) => {
                                        onChange(fieldName, undefined, true, event.target?.value);
                                    }}
                                fieldSelect="dataSource"
                                currentState={column.dataSource}
                                labelField="rowDataSource"
                                showWarning={false}
                                filterable={false}
                                customLabelId="rowDataSource-label-id"
                                customId="rowDataSource-id"
                                customClassName="add-panel-rowDataSource"
                                error={errors[`dataSource${index}`]}
                            />
                        </FormControl>
                        {column.type !== StudyOverviewExtraColumnType.RATE && (
                            <FormControl variant="outlined" className="form-control-type select-with-error">
                                <FieldSelector
                                    label={`${t('admin:columnDataPointCalculationLabel', { num: index + 1 })}*`}
                                    dataSource={column.dataSource}
                                    formats={[Formats.Int, Formats.Float]}
                                    value={column.dataField}
                                    onChange={(value: string) => {
                                        onChange('dataField', undefined, true, value);
                                    }}
                                    labelField="dataField"
                                    fieldSelect="dataField"
                                    customId="add-panel-dataField"
                                    error={errors[`dataField${index}`]}
                                    searchable
                                    disabled={!canSave}
                                />
                            </FormControl>
                        )}
                    </div>
                    {column.type === StudyOverviewExtraColumnType.RATE && (
                        <div className="type-edc-wrapper">
                            <FormControl variant="outlined" className="form-control-type mr select-with-error">
                                <FieldSelector
                                    label={`${t('admin:columnDenominatorLabel', { num: index + 1 })}*`}
                                    dataSource={column.dataSource}
                                    formats={[Formats.Int, Formats.Float]}
                                    value={column.denominatorField}
                                    onChange={(value: string) => {
                                        onChange('denominatorField', undefined, true, value);
                                    }}
                                    labelField="denominatorField"
                                    fieldSelect="denominatorField"
                                    customId="add-panel-denominatorField"
                                    error={errors[`denominatorField${index}`]}
                                    excludeValue={(field: { key: string }) => field.key !== column.numeratorField}
                                    searchable
                                    disabled={!canSave}
                                />
                            </FormControl>
                            <FormControl variant="outlined" className="form-control-type select-with-error">
                                <FieldSelector
                                    label={`${t('admin:columnNumeratorLabel', { num: index + 1 })}*`}
                                    dataSource={column.dataSource}
                                    formats={[Formats.Int, Formats.Float]}
                                    value={column.numeratorField}
                                    onChange={(value: string) => {
                                        onChange('numeratorField', undefined, true, value);
                                    }}
                                    labelField="numeratorField"
                                    fieldSelect="numeratorField"
                                    customId="add-panel-numeratorField"
                                    error={errors[`numeratorField${index}`]}
                                    excludeValue={(field: { key: string }) => field.key !== column.denominatorField}
                                    searchable
                                    disabled={!canSave}
                                />
                            </FormControl>
                        </div>
                    )}
                </>
            )}
        </>
    );
};

export default AddPanelColumn;
